// Spotify login: Authorization Code + PKCE (no client secret anywhere).

export const SCOPES =
  'streaming user-read-email user-read-private user-read-playback-state user-modify-playback-state';

const AUTHORIZE_URL = 'https://accounts.spotify.com/authorize';
const TOKEN_URL = 'https://accounts.spotify.com/api/token';
const ME_URL = 'https://api.spotify.com/v1/me';
const TOKENS_KEY = 'banger.spotify';
const PKCE_KEY = 'banger.spotify.pkce';
const CUSTOM_CLIENT_KEY = 'banger.spotifyClientId';
const CLIENT_ID_RE = /^[0-9a-f]{32}$/i;
const REFRESH_MARGIN_MS = 60_000;

/** `client_id`: the app the tokens were issued for (missing on tokens saved before custom apps existed). */
interface Tokens { access_token: string; refresh_token: string; expires_at: number; client_id?: string }
interface TokenResponse { access_token: string; refresh_token?: string; expires_in: number }

export type AuthFailure = 'not_allowed' | 'failed';
export class SpotifyAuthError extends Error {
  /** `rejected`: Spotify turned the app/login down (bad client ID or redirect URI), not a lost session or network. */
  constructor(public reason: AuthFailure, message: string, public rejected = false) { super(message); }
}

function envClientId(): string {
  return (import.meta.env.VITE_SPOTIFY_CLIENT_ID as string | undefined)?.trim() ?? '';
}

/** A Spotify Client ID looks like 32 hex characters. */
export function isValidClientId(id: string): boolean {
  return CLIENT_ID_RE.test(id.trim());
}

/** The host's own Spotify app ("Use your own Spotify app"), or null. */
function customClientId(): string | null {
  try {
    const id = localStorage.getItem(CUSTOM_CLIENT_KEY)?.trim() ?? '';
    return isValidClientId(id) ? id : null;
  } catch { return null; }
}

export function customClientIdActive(): boolean {
  return customClientId() !== null;
}

/** Stores the host's own Client ID; false (and nothing stored) when it is not a valid one. */
export function setCustomClientId(id: string): boolean {
  const trimmed = id.trim();
  if (!isValidClientId(trimmed)) return false;
  try { localStorage.setItem(CUSTOM_CLIENT_KEY, trimmed); return true; } catch { return false; }
}

/** Back to BANGER!'s own app; the custom app's tokens go too. */
export function clearCustomClientId(): void {
  try { localStorage.removeItem(CUSTOM_CLIENT_KEY); } catch { /* */ }
  logout();
}

/** The custom Client ID when one is set, else the build's VITE_SPOTIFY_CLIENT_ID. */
export function clientId(): string {
  return customClientId() ?? envClientId();
}

function redirectUri(): string {
  return `${location.origin}/callback`;
}

function base64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(byteCount: number): string {
  return base64url(crypto.getRandomValues(new Uint8Array(byteCount)));
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

function loadTokens(): Tokens | null {
  try {
    const t = JSON.parse(localStorage.getItem(TOKENS_KEY) ?? 'null') as Tokens | null;
    if (!t || typeof t.access_token !== 'string' || typeof t.refresh_token !== 'string'
      || typeof t.expires_at !== 'number') return null;
    // Tokens only work with the app that issued them: switching apps means logging in again.
    return (t.client_id ?? envClientId()) === clientId() ? t : null;
  } catch { return null; }
}

function saveTokens(r: TokenResponse, now: number, previousRefresh?: string): void {
  const t: Tokens = {
    access_token: r.access_token,
    refresh_token: r.refresh_token ?? previousRefresh ?? '',
    expires_at: now + r.expires_in * 1000,
    client_id: clientId(),
  };
  try { localStorage.setItem(TOKENS_KEY, JSON.stringify(t)); } catch { /* private mode */ }
}

export function logout(): void {
  try { localStorage.removeItem(TOKENS_KEY); } catch { /* */ }
}

export function isLoggedIn(): boolean {
  return loadTokens() !== null;
}

/** Redirects to Spotify. `showDialog` forces the account picker ("Try a different account"). */
export async function beginLogin(opts: { showDialog?: boolean } = {}): Promise<void> {
  const verifier = randomString(64); // 86 chars, within PKCE's 43–128
  const state = randomString(16);
  try { sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state })); } catch { /* callback will report it */ }
  const params = new URLSearchParams({
    client_id: clientId(),
    response_type: 'code',
    redirect_uri: redirectUri(),
    scope: SCOPES,
    code_challenge_method: 'S256',
    code_challenge: await challengeFor(verifier),
    state,
  });
  if (opts.showDialog) params.set('show_dialog', 'true');
  location.assign(`${AUTHORIZE_URL}?${params}`);
}

async function postToken(body: Record<string, string>): Promise<Response> {
  return fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body).toString(),
  });
}

/** Finishes the redirect: checks state, exchanges the code, stores the tokens. */
export async function handleCallback(url: string): Promise<void> {
  const params = new URL(url).searchParams;
  let pkce: { verifier: string; state: string } | null = null;
  try {
    pkce = JSON.parse(sessionStorage.getItem(PKCE_KEY) ?? 'null');
    sessionStorage.removeItem(PKCE_KEY);
  } catch { /* */ }

  const error = params.get('error');
  if (error) throw new SpotifyAuthError('failed', error, error !== 'access_denied'); // access_denied: the user pressed Cancel
  const code = params.get('code');
  if (!code || !pkce || params.get('state') !== pkce.state) {
    throw new SpotifyAuthError('failed', 'Login could not be verified. Please try again.');
  }

  const now = Date.now();
  const res = await postToken({
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri(),
    client_id: clientId(),
    code_verifier: pkce.verifier,
  });
  if (!res.ok) {
    throw new SpotifyAuthError(
      res.status === 403 ? 'not_allowed' : 'failed', `Token exchange failed (${res.status})`, res.status === 400 || res.status === 401,
    );
  }
  saveTokens((await res.json()) as TokenResponse, now);
}

let refreshing: Promise<string> | null = null;

async function refresh(t: Tokens, now: number): Promise<string> {
  const res = await postToken({ grant_type: 'refresh_token', refresh_token: t.refresh_token, client_id: clientId() });
  if (!res.ok) {
    // Only a dead refresh token logs out; anything else may be transient.
    if (res.status === 400) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      if (body?.error === 'invalid_grant') logout();
    }
    throw new SpotifyAuthError(res.status === 403 ? 'not_allowed' : 'failed', `Token refresh failed (${res.status})`);
  }
  const r = (await res.json()) as TokenResponse;
  saveTokens(r, now, t.refresh_token);
  return r.access_token;
}

/** A valid access token; refreshes when less than 60 s remain (or when `force`d). */
export async function getAccessToken(now: number = Date.now(), force = false): Promise<string> {
  const t = loadTokens();
  if (!t) throw new SpotifyAuthError('failed', 'Not logged in to Spotify.');
  if (!force && t.expires_at - now >= REFRESH_MARGIN_MS) return t.access_token;
  refreshing ??= refresh(t, now).finally(() => { refreshing = null; });
  return refreshing;
}

/**
 * In Spotify development mode a non-allow-listed user can finish OAuth, but
 * Web API calls answer 403. Playback also needs Premium.
 */
export async function checkAccount(): Promise<'ok' | 'not_allowed' | 'not_premium'> {
  const token = await getAccessToken();
  const res = await fetch(ME_URL, { headers: { Authorization: `Bearer ${token}` } });
  if (res.status === 403) return 'not_allowed';
  if (!res.ok) throw new SpotifyAuthError('failed', `Spotify profile request failed (${res.status})`);
  const me = (await res.json()) as { product?: string };
  return me.product === 'premium' ? 'ok' : 'not_premium';
}
